import {
  ButtonERP,
  ButtonFilterERP,
  ButtonPrimeReact,
  InputCurrency,
  SearchInput,
} from "@finanedi.es/storybook"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { AsientosContablesQueryParamsProps } from "api/AsientoContable.service"
import { TicketParamsProps } from "api/Ticket.service"
import { AnimatePresence } from "framer-motion"
import { Calendar } from "primereact/calendar"
import { Dialog } from "primereact/dialog"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"

export const FilterMenu = ({
  serviceQuery,
  sortingCriteria,
  skipCount,
  maxResultCount,
  resetPagination,
  totalCount,
}: {
  serviceQuery: $TSMigrationFixMe
  sortingCriteria?: AsientosContablesQueryParamsProps["sortingCriteria"]
  skipCount?: AsientosContablesQueryParamsProps["skipCount"]
  maxResultCount?: AsientosContablesQueryParamsProps["maxResultCount"]
  resetPagination?: () => void
  totalCount?: number
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const history = useHistory()
  const [menuOpen, setMenuOpen] = useState(false)
  const [amount, setAmount] = useState<Record<string, number | undefined>>({
    min: undefined,
    max: undefined,
  })

  const [date, setDate] = useState<Record<string, string | undefined>>()
  const [keyword, setKeyword] = useState<string | undefined>()

  const openFilters = () => {
    setMenuOpen(true)
  }
  const queryFixer = (queryParams: Partial<TicketParamsProps>) => {
    const ticketsParams = {
      Categorias: queryParams.Categorias,
      FechaEmisionLowerLimit: queryParams.FechaEmisionLowerLimit,
      FechaEmisionUpperLimit: queryParams.FechaEmisionUpperLimit,
      ImporteLowerLimit: queryParams.ImporteLowerLimit,
      ImporteUpperLimit: queryParams.ImporteUpperLimit,
      Keyword: queryParams.Keyword,
      MaxResultCount: queryParams.MaxResultCount,
      SkipCount: queryParams.SkipCount,
      SortingCriteria:
        queryParams.SortingCriteria?.property &&
        queryParams.SortingCriteria?.sort
          ? `${queryParams.SortingCriteria.property} ${queryParams.SortingCriteria.sort}`
          : "FechaEmision DESC",
    }
    const fixedQueryParams = Object.fromEntries(
      Object.entries(ticketsParams).filter(
        ([_key, value]) => value || value === 0 // eslint-disable-line @typescript-eslint/no-unused-vars
      )
    )
    return fixedQueryParams
  }
  const query = {
    Categorias: undefined,
    FechaEmisionLowerLimit: date?.min,
    FechaEmisionUpperLimit: date?.max,
    ImporteLowerLimit: amount.min,
    ImporteUpperLimit: amount.max,
    SortingCriteria: sortingCriteria,
    SkipCount: skipCount,
    MaxResultCount: maxResultCount,
    Keyword: keyword,
  }
  const handleQuery = () => {
    serviceQuery(queryFixer(query))
  }
  const handleApplyFilters = () => {
    resetPagination?.()
    handleQuery()
    setMenuOpen(false)
  }
  const hideFilter = () => {
    setMenuOpen(!menuOpen)
  }
  const handleResetFilters = () => {
    serviceQuery(queryFixer({}))
    setAmount({ min: undefined, max: undefined })
    setDate({ min: undefined, max: undefined })
    resetPagination?.()
  }
  const appliedFiltersTags = [
    {
      label: t("tickets.filter.amountFrom"),
      value: amount?.min
        ? Intl.NumberFormat("de-DE", {
            style: "currency",
            currency: "EUR",
          }).format(amount?.min)
        : undefined,
      action: () => setAmount({ ...amount, min: undefined }),
    },
    {
      label: t("tickets.filter.amountTo"),
      value: amount?.max
        ? Intl.NumberFormat("de-DE", {
            style: "currency",
            currency: "EUR",
          }).format(amount?.max)
        : undefined,
      action: () => setAmount({ ...amount, max: undefined }),
    },
    {
      label: t("tickets.filter.dateFrom"),
      value: date?.min
        ? Intl.DateTimeFormat("es-ES", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }).format(new Date(date.min))
        : undefined,
      action: () => setDate({ ...date, min: undefined }),
    },
    {
      label: t("tickets.filter.dateTo"),
      value: date?.max
        ? Intl.DateTimeFormat("es-ES", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }).format(new Date(date.max))
        : undefined,
      action: () => setDate({ ...date, max: undefined }),
    },
  ]
  const appliedFilters = appliedFiltersTags.filter((filter) => filter.value)

  useEffect(() => {
    resetPagination?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // FIX: Se reinicia la paginacion y el ordenamiento al cambiar de pagina pero el sorter aparece activo
    // sortingCriteria?.sort,
    // sortingCriteria?.property,
    keyword,
    amount?.min,
    amount?.max,
    date?.min,
    date?.max,
  ])
  useEffect(() => {
    handleQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sortingCriteria?.sort,
    sortingCriteria?.property,
    keyword,
    amount?.min,
    amount?.max,
    date?.min,
    date?.max,
    skipCount,
    maxResultCount,
  ])

  return (
    <>
      <div className='!tw-px-6 tw-gap-2 tw-flex tw-flex-col lg:tw-flex-row tw-justify-between tw-pb-4 tw-pt-6 tw-shadow-3xl tw-rounded-t-xl mb-1 tw-bg-white tw-relative'>
        <p className='!tw-items-left tw-my-0 tw-whitespace-nowrap tw-text-xl tw-text-text tw-flex tw-items-center tw-gap-2'>
          {t("tickets.title")}{" "}
          {totalCount && <span className='lg:tw-hidden'>({totalCount})</span>}
        </p>
        <div className='tw-flex tw-flex-row tw-items-center tw-w-full lg:tw-w-fit tw-gap-1 lg:tw-gap-3'>
          <ButtonFilterERP
            isActive={appliedFilters.length > 0}
            numberActive={appliedFilters?.length}
            onClick={openFilters}
            deleteFilter={handleResetFilters}
            filterButtonLabel={`${t("filtros.title")}`}
          />
          <SearchInput
            placeholder={`${t("tickets.filter.searchLabel")}`}
            onChange={(ev: string) => {
              resetPagination?.()
              setKeyword(ev)
            }}
            inputValue={keyword}
            className='tw-h-10 tw-w-full lg:tw-w-72'
          />
          <div className='xl:tw-flex tw-gap-2 tw-hidden lg:tw-grid lg:tw-grid-cols-3'>
            <ButtonERP
              customize={{
                size: "md",
                type: "secondary",
              }}
              onClick={() => history.push(ROUTES.CREAR_TICKET)}
              label={`${t("tickets.filter.addTicket")}`}
            />
          </div>
        </div>
      </div>

      <AnimatePresence>
        <Dialog
          breakpoints={{ "641px": "75vw", "640px": "100vw" }}
          className='p-dialog-right tw-select-none'
          header={t("filtros.title")}
          visible={menuOpen}
          footer={
            <div className='position-bottom tw-w-full tw-border-t-2 tw-pt-6'>
              <ButtonPrimeReact
                className='tw-ml-2 !tw-bg-primary tw-cursor-pointer !tw-w-full'
                onClick={handleApplyFilters}
              >
                {t("filtros.labelApply")}
              </ButtonPrimeReact>
            </div>
          }
          onHide={() => {
            hideFilter()
          }}
          draggable={false}
          resizable={false}
          position='right'
          closable
          dismissableMask
          closeOnEscape
        >
          {!!appliedFilters?.length && (
            <div className='tw-mb-8'>
              <div className='tw-flex tw-mb-4 tw-justify-between'>
                <span className='tw-font-medium'>
                  {t("tickets.filter.appliedFilters")}
                </span>
                <span
                  onClick={handleResetFilters}
                  className='tw-text-text-hyperlinks tw-cursor-pointer'
                >
                  {t("tickets.filter.deleteAll")}
                </span>
              </div>
              <div className='tw-flex tw-flex-wrap tw-mb-8 tw-gap-2 tw-font-normal tw-text-xs'>
                {appliedFilters.map((filter) => (
                  <div
                    key={filter.label}
                    onClick={filter.action}
                    className='tw-items-center tw-justify-between tw-cursor-pointer tw-bg-info-10 tw-text-info tw-rounded-md tw-border tw-border-info tw-py-1 tw-px-2'
                  >
                    <span>
                      {filter.label} {filter.value}
                    </span>
                    <FontAwesomeIcon
                      icon={["fal", "xmark"]}
                      className='tw-ml-2 tw-text-info'
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("bancos.conciliacion.filterMenu.amountTitle")}
              </span>
            </div>
            <div className='tw-flex tw-flex-col mobile:tw-flex-row tw-mb-8'>
              <div className='tw-flex tw-flex-col tw-flex-1 tw-mb-2 mobile:tw-mb-0'>
                <label>
                  {t("bancos.conciliacion.filterMenu.amountLabelSince")}
                </label>
                <InputCurrency
                  placeholder='0,00'
                  value={amount.min}
                  className='!tw-w-full !tw-h-10 !tw-rounded'
                  suffix='€'
                  min={0}
                  max={amount.max ?? Number.MAX_SAFE_INTEGER}
                  onChange={(value) => setAmount({ ...amount, min: value })}
                />
              </div>
              <div className='tw-hidden mobile:tw-block tw-py-3 tw-px-5 tw-items-baseline tw-mt-auto tw-mb-0'>
                -
              </div>
              <div className='tw-flex tw-flex-col tw-flex-1'>
                <label>
                  {t("bancos.conciliacion.filterMenu.amountLabelUntil")}
                </label>
                <InputCurrency
                  placeholder='0,00'
                  value={amount.max}
                  className='!tw-w-full !tw-h-10 !tw-rounded'
                  suffix='€'
                  min={amount?.min ?? 0}
                  max={Number.MAX_SAFE_INTEGER}
                  onChange={(value) => setAmount({ ...amount, max: value })}
                />
              </div>
            </div>
          </div>

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("tickets.filter.issueDate")}
              </span>
            </div>
            <div className='tw-flex tw-flex-col mobile:tw-flex-row tw-mb-6'>
              <div className='tw-flex tw-flex-col tw-mb-2 mobile:tw-mb-0 tw-flex-1'>
                <label>
                  {t("bancos.conciliacion.filterMenu.dateLabelSince")}
                </label>
                <Calendar
                  id='fechaDesde'
                  name='fechaDesde'
                  clearButtonClassName='!tw-hidden'
                  value={date?.min ? new Date(date.min) : undefined}
                  locale={language}
                  onChange={(e) => {
                    setDate({
                      ...date,
                      min: e.target.value as unknown as string,
                    })
                  }}
                  placeholder='dd/mm/aaaa'
                  dateFormat='dd/mm/yy'
                  mask='99/99/9999'
                  className='w-full'
                  showIcon
                  panelClassName='!tw-z-2302'
                />
              </div>
              <div className='tw-hidden mobile:tw-block tw-py-3 tw-px-3 tw-items-baseline tw-mt-auto tw-mb-0'>
                -
              </div>
              <div className='tw-flex tw-flex-col tw-flex-1'>
                <label>
                  {t("bancos.conciliacion.filterMenu.dateLabelUntil")}
                </label>
                <Calendar
                  id='fechaHasta'
                  name='fechaHasta'
                  clearButtonClassName='!tw-hidden'
                  value={date?.max ? new Date(date.max) : undefined}
                  locale={language}
                  onChange={(e) => {
                    setDate({
                      ...date,
                      max: e.target.value as unknown as string,
                    })
                  }}
                  placeholder='dd/mm/aaaa'
                  dateFormat='dd/mm/yy'
                  mask='99/99/9999'
                  className='w-full'
                  showIcon
                  panelClassName='!tw-z-2302'
                />
              </div>
            </div>
          </div>
        </Dialog>
      </AnimatePresence>
    </>
  )
}
